/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useGuidesStore } from '@/stores/guides.store';
import { createGuide, createStep } from '@/guides/helpers/guide-editor.util';
import { copyVocItemMedia } from '@/voc/api/media.api';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
// @ts-expect-error missing type declarations for BuildingBlocks
import { BuildingBlock, BuildingBlockLayouts, Polls } from '@pendo/services/BuildingBlocks';
import { $can, $canSub } from '@pendo/permissions';
import moment from 'moment-timezone';
import { dateOneIsEarlierOrSame, normalizeDateTime } from '@/guides/helpers/common.util';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import VocItemsCountAggregation from '@/voc/aggregations/voc-items-count/voc-items-count.aggregation';

const { VALIDATE } = SUB_PRODUCT_AREAS || {};

interface Details {
    name: string;
    appId: string | null;
    theme: any;
    complete: boolean;
}

interface Items {
    title: string;
    description: string;
    vocItems: any[];
    isSinglePageLayout: boolean;
    complete: boolean;
}

interface Scheduling {
    showsAfter: string;
    showsAfterTime: string;
    expiresAfter: string;
    expiresAfterTime: string;
    hasExpiresAfter: boolean;
    hasMadeEditsToStartDateTime: boolean;
    hasMadeEditsToExpirationDateTime: boolean;
}

interface SavedStepsCheatSheet {
    title: string;
    description: string;
    theme: any;
    vocItemIds: string[];
    isSinglePageLayout: boolean;
}

interface VocPollWizardState {
    details: Details;
    appHasIdeas: boolean;
    items: Items;
    segment: Record<string, any>;
    scheduling: Scheduling;
    audience: any[];
    audienceUiHint: any[];
    currentStepIndex: number;
    steps: string[];
    guide: any;
    saveInProgress: boolean;
    savedStepsCheatSheet: SavedStepsCheatSheet;
    wizardOpen: boolean;
}

export function getInitialState (): VocPollWizardState {
    return {
        details: {
            name: '',
            appId: null,
            theme: null,
            complete: false,
        },
        appHasIdeas: true,
        items: {
            title: '',
            description: '',
            vocItems: [],
            isSinglePageLayout: true,
            complete: false,
        },
        segment: {},
        scheduling: {
            showsAfter: '',
            showsAfterTime: '',
            expiresAfter: '',
            expiresAfterTime: '',
            hasExpiresAfter: false,
            hasMadeEditsToStartDateTime: false,
            hasMadeEditsToExpirationDateTime: false,
        },
        audience: [],
        audienceUiHint: [],
        currentStepIndex: 0,
        steps: ['Details', 'Content', 'Segment', 'Schedule'],
        guide: null,
        saveInProgress: false,
        savedStepsCheatSheet: {
            title: '',
            description: '',
            theme: null,
            vocItemIds: [],
            isSinglePageLayout: true,
        },
        wizardOpen: false,
    };
}

export const useVocPollWizardStore = defineStore('vocPollWizard', {
    state: getInitialState,
    getters: {
        detailsComplete: (state) => state.details.appId !== null && state.details.name !== '' && state.appHasIdeas,
        validStartDate: (state) => {
            if (!state.scheduling.hasMadeEditsToStartDateTime) return true;

            return moment(state.scheduling.showsAfter).isSameOrAfter(moment(), 'day');
        },
        validStartTime: (state) => {
            if (!state.scheduling.hasMadeEditsToStartDateTime) return true;

            return moment(normalizeDateTime(state.scheduling.showsAfter, state.scheduling.showsAfterTime)).isAfter(
                moment(),
            );
        },
        validExpirationDate: (state) => {
            if (!state.scheduling.hasExpiresAfter || !state.scheduling.expiresAfter) return true;
            if (!state.scheduling.hasMadeEditsToExpirationDateTime && !state.scheduling.hasMadeEditsToStartDateTime) {
                return true;
            }
            const sameOrAfter = state.scheduling.showsAfter ? moment(state.scheduling.showsAfter) : moment();

            return moment(state.scheduling.expiresAfter).isSameOrAfter(sameOrAfter, 'day');
        },
        validExpirationTime (state): boolean {
            if (!state.scheduling.hasExpiresAfter) return true;
            if (state.scheduling.expiresAfterTime && !state.scheduling.expiresAfter) return false;
            if (!(this as any).validExpirationDate) return false;
            if (!state.scheduling.hasMadeEditsToExpirationDateTime) return true;

            if (
                state.scheduling.hasExpiresAfter &&
                moment(state.scheduling.showsAfter).isSameOrAfter(moment(), 'day')
            ) {
                return !dateOneIsEarlierOrSame(
                    state.scheduling.expiresAfter,
                    state.scheduling.expiresAfterTime,
                    state.scheduling.showsAfter,
                    state.scheduling.showsAfterTime,
                );
            }

            return moment(normalizeDateTime(state.scheduling.expiresAfter, state.scheduling.expiresAfterTime)).isAfter(
                moment(),
            );
        },
        schedulingHasErrors (): boolean {
            return (
                !(this as any).validStartDate ||
                !(this as any).validStartTime ||
                !(this as any).validExpirationDate ||
                !(this as any).validExpirationTime
            );
        },
        guideUrl: (state) => {
            if (state.guide === null) {
                return null;
            }

            return `/listen/validate/ideatests/${state.guide.id}?view=settings`;
        },
        hasCreateEditAppPerm (): boolean {
            return (this as any).filteredAppList.length > 0;
        },
        filteredAppList () {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when apps migrates to Pinia

            return vuexStore?.getters?.['apps/onlyIntegratedApps'] ?? [];
        },
        canCreatePoll: () =>
            $canSub()
                .entity('ideaPoll')
                .action('create'),
        canCreateGuide: () => (appId: string) =>
            ($can(appId)
                .entity('guide')
                .action('create') as any)
                .source('layout')
                .target('*'),
        canEditGuide: () => (appId: string) =>
            ($can(appId)
                .entity('guide')
                .action('edit') as any)
                .source('draft')
                .target('draft'),
        canRemoveGuide: () => (appId: string) =>
            ($can(appId)
                .entity('guide')
                .action('remove') as any)
                .source('draft')
                .target('draft'),
        canCreateStep: () => (appId: string) =>
            ($can(appId)
                .entity('guideStep')
                .action('create') as any)
                .source('layout')
                .target('*'),
        isWizardEnabled (): boolean {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useVocItemsStore } = require('@/stores/voc-items.store');

            return (this as any).canCreatePoll && useVocItemsStore().vocItemCount > 0;
        },
        isSinglePageLayout: (state) => state.items.isSinglePageLayout,
    },
    actions: {
        setDetails (details: Partial<Details>) {
            this.details = { ...this.details, ...details };
        },
        setItems (items: Items) {
            this.items = items;
        },
        setSegment ({ segment, audience, audienceUiHint }: { segment: Record<string, any>; audience: any[]; audienceUiHint: any[] }) {
            this.segment = segment;
            this.audience = audience;
            this.audienceUiHint = audienceUiHint;
        },
        setScheduling (scheduling: Scheduling) {
            this.scheduling = scheduling;
        },
        setCurrentStepIndex (index: number) {
            this.currentStepIndex = index;
        },
        setGuide (guide: any) {
            this.guide = guide;
        },
        setSaveInProgress (saveInProgress: boolean) {
            this.saveInProgress = saveInProgress;
        },
        setSavedStepsCheatSheet (cheatSheet: SavedStepsCheatSheet) {
            this.savedStepsCheatSheet = cheatSheet;
        },
        setAppHasIdeas (appHasIdeas: boolean) {
            this.appHasIdeas = appHasIdeas;
        },
        setWizardOpen (wizardOpen: boolean) {
            this.wizardOpen = wizardOpen;
        },
        async saveAppIdDetails (appId: string | null = null) {
            if (appId !== this.details.appId) {
                this.setItems({
                    title: '',
                    description: '',
                    vocItems: [],
                    isSinglePageLayout: true,
                    complete: false,
                });
                this.setSavedStepsCheatSheet({
                    title: '',
                    description: '',
                    theme: null,
                    isSinglePageLayout: true,
                    vocItemIds: [],
                });
                this.setSegment({
                    segment: {},
                    audience: [],
                    audienceUiHint: [],
                });
                this.setScheduling({
                    showsAfter: '',
                    showsAfterTime: '',
                    expiresAfter: '',
                    expiresAfterTime: '',
                    hasExpiresAfter: false,
                    hasMadeEditsToStartDateTime: false,
                    hasMadeEditsToExpirationDateTime: false,
                });
                this.setSaveInProgress(true);
                try {
                    VocItemsCountAggregation.subProductArea = VALIDATE;
                    const vocItemCount = (await runAggregationOnce(VocItemsCountAggregation, { appId }) as any).count;
                    this.setAppHasIdeas(vocItemCount > 0);
                } finally {
                    this.setSaveInProgress(false);
                }
            }

            this.setDetails({ appId });
        },
        saveDetailsNameTheme ({ name = '', theme = null }: { name?: string; theme?: any }) {
            this.setDetails({ name, theme });
        },
        saveItems ({
            vocItems = [],
            title = '',
            description = '',
            isSinglePageLayout = true,
            formValidated = false,
        }: {
            vocItems?: any[];
            title?: string;
            description?: string;
            isSinglePageLayout?: boolean;
            formValidated?: boolean;
        }) {
            const complete = vocItems.length > 0 && formValidated;
            this.setItems({ title, description, vocItems, isSinglePageLayout, complete });
        },
        saveSegment ({
            segment = {},
            audience = [],
            audienceUiHint = [],
        }: {
            segment?: Record<string, any>;
            audience?: any[];
            audienceUiHint?: any[];
        }) {
            this.setSegment({ segment, audience, audienceUiHint });
        },
        saveScheduling ({
            showsAfter = '',
            showsAfterTime = '',
            expiresAfter = '',
            expiresAfterTime = '',
            hasExpiresAfter = false,
            hasMadeEditsToStartDateTime = false,
            hasMadeEditsToExpirationDateTime = false,
        }: Partial<Scheduling> = {}) {
            this.setScheduling({
                showsAfter,
                showsAfterTime,
                expiresAfter,
                expiresAfterTime,
                hasExpiresAfter,
                hasMadeEditsToStartDateTime,
                hasMadeEditsToExpirationDateTime,
            });
        },
        reset () {
            this.setDetails({ name: '', appId: null, theme: null, complete: false });
            this.setItems({ title: '', description: '', vocItems: [], isSinglePageLayout: true, complete: false });
            this.setCurrentStepIndex(0);
            this.setGuide(null);
            this.setSavedStepsCheatSheet({
                title: '',
                description: '',
                theme: null,
                vocItemIds: [],
                isSinglePageLayout: true,
            });
            this.setSegment({ segment: {}, audience: [], audienceUiHint: [] });
            this.setScheduling({
                showsAfter: '',
                showsAfterTime: '',
                expiresAfter: '',
                expiresAfterTime: '',
                hasExpiresAfter: false,
                hasMadeEditsToStartDateTime: false,
                hasMadeEditsToExpirationDateTime: false,
            });
            this.setAppHasIdeas(true);
        },
        async goForwardOneStep () {
            await this.saveGuide();

            if (this.currentStepIndex < this.steps.length - 1) {
                this.setCurrentStepIndex(this.currentStepIndex + 1);
            }
        },
        goBackOneStep () {
            if (this.currentStepIndex > 0) {
                this.setCurrentStepIndex(this.currentStepIndex - 1);
            }
        },
        async saveGuide () {
            try {
                this.setSaveInProgress(true);
                if (this.currentStepIndex === 0) {
                    if (this.guide === null) {
                        await this.initGuide();
                    } else if (this.guide.appId !== this.details.appId) {
                        await this.deleteGuide();
                        await this.initGuide();
                    } else if (this.guide.name !== this.details.name) {
                        await this.updateGuideName();
                    }
                } else if (this.currentStepIndex === 1) {
                    if (
                        this.savedStepsCheatSheet.title !== this.items.title ||
                        this.savedStepsCheatSheet.description !== this.items.description ||
                        this.savedStepsCheatSheet.theme !== this.details.theme ||
                        this.savedStepsCheatSheet.isSinglePageLayout !== this.items.isSinglePageLayout ||
                        !isEqual(
                            this.savedStepsCheatSheet.vocItemIds,
                            this.items.vocItems.map((item) => item.id),
                        )
                    ) {
                        await this.updateGuideSteps();
                    }
                } else if (this.currentStepIndex === 2) {
                    if (!isEmpty(this.segment) && (!this.guide.segment || this.guide.segment !== this.segment)) {
                        await this.updateGuideSegment();
                    }
                } else if (this.currentStepIndex === 3) {
                    if (
                        (this.scheduling.hasMadeEditsToStartDateTime ||
                            this.scheduling.hasMadeEditsToExpirationDateTime) &&
                        !this.schedulingHasErrors
                    ) {
                        await this.updateGuideSchedule();
                    }
                }
            } finally {
                this.setSaveInProgress(false);
            }
        },
        async initGuide () {
            const ideaGuide = await createGuide({
                guideType: 'building-block',
                app: {
                    id: this.details.appId,
                },
            } as any);

            ideaGuide.attributes.type = 'building-block';
            ideaGuide.name = this.details.name;
            ideaGuide.steps = [];
            ideaGuide.polls = [
                {
                    attributes: {
                        type: 'Vote',
                    },
                },
            ];
            const createdGuide = await useGuidesStore().saveGuide({ guide: ideaGuide });
            if (createdGuide) {
                this.setGuide(createdGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test');
            }
        },
        async deleteGuide () {
            if (this.guide) {
                await useGuidesStore().remove({ guide: this.guide });
                this.setGuide(null);
            }
        },
        async updateGuideName () {
            const tmpGuide = cloneDeep(this.guide);
            tmpGuide.name = this.details.name;
            const patchedGuide = await useGuidesStore().patchGuide({
                guide: tmpGuide,
                fieldsToPatch: ['name'],
            });

            if (patchedGuide) {
                this.setGuide(patchedGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test');
            }
        },
        async updateGuideSegment () {
            const tmpGuide = cloneDeep(this.guide);
            tmpGuide.segment = this.segment;
            tmpGuide.audience = this.audience;
            tmpGuide.audienceUiHint = this.audienceUiHint;
            const patchedGuide = await useGuidesStore().patchGuide({
                guide: tmpGuide,
                fieldsToPatch: ['segment', 'audience', 'audienceUiHint'],
            });

            if (patchedGuide) {
                this.setGuide(patchedGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test Segment');
            }
        },
        async updateGuideSchedule () {
            const tmpGuide = cloneDeep(this.guide);
            const showsAfter =
                this.scheduling.showsAfter || this.scheduling.showsAfterTime
                    ? normalizeDateTime(this.scheduling.showsAfter, this.scheduling.showsAfterTime)
                    : undefined;
            const expiresAfter =
                this.scheduling.hasExpiresAfter && this.scheduling.hasMadeEditsToExpirationDateTime
                    ? normalizeDateTime(this.scheduling.expiresAfter, this.scheduling.expiresAfterTime)
                    : undefined;

            tmpGuide.showsAfter = showsAfter;
            tmpGuide.expiresAfter = expiresAfter;

            const patchedGuide = await useGuidesStore().patchGuide({
                guide: tmpGuide,
                fieldsToPatch: ['showsAfter', 'expiresAfter'],
            });

            if (patchedGuide) {
                this.setGuide(patchedGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test Scheduling');
            }
        },
        async updateGuideSteps () {
            /* eslint-disable no-await-in-loop */
            const tmpGuide = cloneDeep(this.guide);
            tmpGuide.polls = [];

            for (let i = 0; i < tmpGuide.steps.length; i++) {
                await useGuidesStore().removeGuideStep({
                    remoteGuide: tmpGuide,
                    step: tmpGuide.steps[i],
                });
            }
            tmpGuide.steps = [];

            const landingStep = await createStep({ guide: tmpGuide, type: 'building-block' } as any) as any;
            landingStep.attributes.themeId = this.details.theme.id;
            landingStep.content = null;
            tmpGuide.steps.push(landingStep);

            const ideasBlock: any[] = [];
            const ideaSteps: any[] = [];
            const pollIdsSinglePage: string[] = [];
            const ideaStepData: any[] = [];
            for (let i = 0; i < this.items.vocItems.length; i++) {
                const votePollId = Math.random()
                    .toString(36)
                    .substring(2);
                const textPollId = Math.random()
                    .toString(36)
                    .substring(2);

                const ideaStep = await createStep({ guide: tmpGuide, type: 'building-block' } as any) as any;
                ideaStep.attributes.themeId = this.details.theme.id;
                ideaStep.content = null;
                ideaStep.pollIds = [votePollId, textPollId];
                tmpGuide.steps.push(ideaStep);

                let bannerImageUrl: string | undefined;
                if (this.items.vocItems[i].bannerImageId) {
                    const bannerImage = await copyVocItemMedia({
                        guideId: tmpGuide.id,
                        vocItemId: this.items.vocItems[i].id,
                        mediaFileType: 'bannerImage',
                        mediaFileId: this.items.vocItems[i].bannerImageId,
                    });
                    bannerImageUrl = bannerImage.data.url;
                }

                ideaSteps.push(ideaStep);
                const pollIdRandNumber = Math.random()
                    .toString(36)
                    .substring(2);
                const pollIdSinglePage = `${this.items.vocItems[i].id}__${pollIdRandNumber}`;
                pollIdsSinglePage.push(pollIdSinglePage);
                ideasBlock.push({
                    vocItem: {
                        title: this.items.vocItems[i].vocItemTitle,
                        description: this.items.vocItems[i].vocItemDescription,
                        id: this.items.vocItems[i].id,
                    },
                    stepId: ideaStep.id,
                    pollId: pollIdSinglePage,
                });
                ideaStepData.push({ vocItem: this.items.vocItems[i], bannerImageUrl, votePollId, textPollId, ideaStep });
            }

            const successStep = await createStep({ guide: tmpGuide, type: 'building-block' } as any) as any;
            successStep.attributes.themeId = this.details.theme.id;
            successStep.content = null;
            successStep.pollIds = [];
            tmpGuide.steps.push(successStep);

            for (const { vocItem, bannerImageUrl, votePollId, textPollId, ideaStep } of ideaStepData) {
                const ideaLayout = BuildingBlockLayouts.createBaseBuildingBlocksForLayout(
                    'ideasDetails',
                    tmpGuide.id,
                    {
                        vocItem: {
                            id: vocItem.id,
                            title: vocItem.vocItemTitle,
                            description: vocItem.vocItemDescription,
                            bannerImageUrl,
                        },
                        baseStepId: landingStep.id,
                        successStepId: successStep.id,
                        successStepIndex: this.items.vocItems.length + 2,
                        votePollId,
                        textPollId,
                        isSinglePageLayout: this.items.isSinglePageLayout,
                    },
                    this.details.theme,
                );
                ideaStep.buildingBlocks = JSON.stringify(ideaLayout);
                ideaStep.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(ideaLayout));
                tmpGuide.polls.push(...Polls.generatePollsForBlocks(ideaLayout));
            }

            const landingPageLayout = BuildingBlockLayouts.createBaseBuildingBlocksForLayout(
                'ideas',
                tmpGuide.id,
                {
                    title: this.items.title,
                    subTitle: this.items.description,
                    ideasBlock,
                    isSinglePageLayout: this.items.isSinglePageLayout,
                    successStepId: successStep.id,
                    successStepIndex: this.items.vocItems.length + 2,
                },
                this.details.theme,
            );
            landingStep.pollIds = pollIdsSinglePage;
            landingStep.buildingBlocks = JSON.stringify(landingPageLayout);
            landingStep.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(landingPageLayout));
            tmpGuide.polls.push(...Polls.generatePollsForBlocks(landingPageLayout));

            const successMessageLayout = BuildingBlockLayouts.createBaseBuildingBlocksForLayout(
                'ideasSuccessMessage',
                tmpGuide.id,
                {
                    title: 'Thank you for your feedback',
                    subTitle: 'Building great products is hard and your feedback will help us grow!',
                    landingStepId: landingStep.id,
                    isSinglePageLayout: this.items.isSinglePageLayout,
                },
                this.details.theme,
            );
            successStep.buildingBlocks = JSON.stringify(successMessageLayout);
            successStep.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(successMessageLayout));

            tmpGuide.steps = [landingStep, ...ideaSteps, successStep];
            for (let i = 0; i < tmpGuide.steps.length; i++) {
                const savedStep = await useGuidesStore().saveGuideStep({
                    remoteGuide: tmpGuide,
                    updatedStep: tmpGuide.steps[i],
                });
                tmpGuide.steps[i] = savedStep;
            }

            const patchedGuide = await useGuidesStore().patchGuide({
                guide: tmpGuide,
                fieldsToPatch: ['polls'],
            });
            if (patchedGuide) {
                this.setGuide(patchedGuide);
                this.setSavedStepsCheatSheet({
                    title: this.items.title,
                    description: this.items.description,
                    theme: this.details.theme,
                    isSinglePageLayout: this.items.isSinglePageLayout,
                    vocItemIds: this.items.vocItems.map((item) => item.id),
                });
            } else {
                throw new Error('An error occurred while saving the Idea Test');
            }
            /* eslint-enable no-await-in-loop */
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useVocPollWizardStore, module.hot));
}
